import {
  IonicModule,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { CommonModule } from '@angular/common';

import { Component, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { UtilService } from 'src/app/services/util/util-service';
import FuelPlacesModalComponent from './fuel-places-modal/fuel-places-modal.component';
import { HttpClient } from '@angular/common/http';

import * as en from './i18n/en.json';
import * as es from './i18n/es.json';
import * as pt from './i18n/pt.json';
import { catchError, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { RequestToBridgeService } from 'src/app/services/request-to-bridge/request-to-bridge.service';

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HeaderComponent } from '../../components/header/header.component';

@Component({
    selector: 'app-register-fuel',
    templateUrl: './register-fuel.component.html',
    styleUrls: ['./register-fuel.component.scss'],
    imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    HeaderComponent,
    ReactiveFormsModule,
    TranslateModule
]
})
export default class RegisterFuelComponent implements OnInit {
  customerColor = localStorage.getItem('cor');
  fuelForm!: FormGroup;
  fuelTypes = [];

  proofFuelRegisterImgB64: string; // base64
  vehicleName = '';
  customerId: number;

  state;

  select;
  suppliersList = [];
  driversList = [];
  fuelPlaceLabel = '';
  vehicleId = 0;

  vehicleList = [];

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private util: UtilService,
    private modalCtrl: ModalController,
    private http: HttpClient,
    private loadingCtrl: LoadingController,
    private bridgeApi: RequestToBridgeService,
  ) {
    this.loadLocalTranslations();
  }

  async ngOnInit() {
    this.state = window.history.state;

    if (!this.state.vehicleId && !this.state.customerId) {
      console.warn('necessary data was not provided(customer and vehicle id)');
      window.history.back();
      return;
    }

    this.fuelForm = this.fb.group({
      vehicleId: ['', Validators.required],
      driverId: ['', Validators.required],
      fuelAddressId: ['', Validators.required],
      supplierId: ['', Validators.required],
      nfNumber: ['', Validators.required],
      fuelLiters: ['', [Validators.required]],
      fuelTotalPrice: ['', [Validators.required]],
      fuelTypeSupplied: ['', [Validators.required]],
      hodometer: [''],
      horimetro: [''],
      observation: [''],
      proofFuelRegister: ['', [Validators.required]], // base64
    });

    this.customerId = this.state.customerId;

    this.vehicleList = [
      {
        id: this.state.vehicleId,
        name: this.state.vehicleName,
      },
    ];

    this.select = await firstValueFrom(this.translate.get("REGISTER-FUEL--SELECT"));


    const gasolinaComum = await firstValueFrom(this.translate.get("REGISTER-FUEL--REGULAR-GASOLINE"));
    const gasolinaAditivada = await firstValueFrom(this.translate.get("REGISTER-FUEL--ADDITIVE-GASOLINE"));
    const gasolinaPremium = await firstValueFrom(this.translate.get("REGISTER-FUEL--PREMIUM-GASOLINE"));
    const dieselComum = await firstValueFrom(this.translate.get("REGISTER-FUEL--REGULAR-DIESEL"));
    const dieselSTen = await firstValueFrom(this.translate.get("REGISTER-FUEL--DIESEL-S10"));
    const dieselAditivado = await firstValueFrom(this.translate.get("REGISTER-FUEL--ADDITIVE-DIESEL"));
    const dieselPremium = await firstValueFrom(this.translate.get("REGISTER-FUEL--PREMIUM-DIESEL"));
    const etanolComum = await firstValueFrom(this.translate.get("REGISTER-FUEL--REGULAR-ETHANOL"));
    const etanolAditivado = await firstValueFrom(this.translate.get("REGISTER-FUEL--ADDITIVE-ETHANOL"));
    const alcool = await firstValueFrom(this.translate.get("REGISTER-FUEL--ALCOHOL"));
    const gnv = await firstValueFrom(this.translate.get("REGISTER-FUEL--CNG"));
    const arla = await firstValueFrom(this.translate.get("REGISTER-FUEL--ARLA"));

    this.fuelTypes = [
      gasolinaComum,
      gasolinaAditivada,
      gasolinaPremium,
      dieselComum,
      dieselSTen,
      dieselAditivado,
      dieselPremium,
      etanolComum,
      etanolAditivado,
      alcool,
      gnv,
      arla
    ];
  }

  async ionViewWillEnter() {
    await this.loadData();
  }

  async loadData() {
    const msg = await firstValueFrom(this.translate.get("REGISTER-FUEL--FETCHING-DATA"));

    const loading = await this.loadingCtrl.create({
      message: msg,
    });

    await loading.present();

    const requests = forkJoin({
      resSuppliers: this.getSuppliers().pipe(
        catchError((err) => of({ success: false, error: err })),
      ),
      resDrivers: this.getDrivers().pipe(
        catchError((err) => of({ success: false, error: err })),
      ),
    });

    requests.subscribe(async (result) => {
      await loading.dismiss();

      const { resSuppliers, resDrivers } = result;

      if (!resSuppliers.success || !resDrivers.success) {
        const msgError = await firstValueFrom(this.translate.get("REGISTER-FUEL--FETCH-DATA-FAILED"));

        await this.util.showToastError(msgError);
        return;
      }
      this.suppliersList = resSuppliers.suppliers_list;
      this.driversList = resDrivers.drivers_list;
    });
  }

  getDrivers(): Observable<any> {
    const requestData = {
      customer_id: localStorage.getItem('id_cliente'),
      hash_session: localStorage.getItem('hash'),
    };

    return this.bridgeApi.get('api/motoristas', requestData);
  }

  getSuppliers(): Observable<any> {
    const requestData = {
      customer_id: localStorage.getItem('id_cliente'),
      hash_session: localStorage.getItem('hash'),
    };

    return this.bridgeApi.get('api/fornecedores', requestData);
  }

  loadLocalTranslations() {
    this.translate.setTranslation('en', en, true);
    this.translate.setTranslation('pt', pt, true);
    this.translate.setTranslation('es', es, true);
  }

  async takePicture() {
    try {
      const image = await Camera.getPhoto({
        quality: 99,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 1024,
        height: 1024,
      });

      this.proofFuelRegisterImgB64 = await this.watermarkImage(image.dataUrl);

      console.log(this.proofFuelRegisterImgB64);

      this.fuelForm.patchValue({
        proofFuelRegister: this.proofFuelRegisterImgB64,
      });
    } catch (error) {
      console.warn('Error taking picture:', error);
      this.proofFuelRegisterImgB64 = '';
    }
  }

  async watermarkImage(originalImageBase64: string): Promise<string> {
    const nowDate = this.getCurrentDateTime();

    const { latitude, longitude, address } = await this.getAddress();

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Erro ao obter contexto do canvas');

    const originalImage = await this.loadImage(originalImageBase64);

    canvas.width = originalImage.width;
    canvas.height = originalImage.height;

    context.drawImage(originalImage, 0, 0, canvas.width, canvas.height);

    context.font = '16px Arial';
    context.textAlign = 'left';

    const margin = 30;
    const padding = 30;
    const addressLines = this.getLines(context, address, canvas.width - 90);

    // primeiro layer branco
    context.fillStyle = 'rgba(255, 255, 255, 0.8)';

    addressLines.reverse().forEach((line, idx) => {
      context.fillText(line, margin, canvas.height - padding * (idx + 3));
    });

    context.fillText(`${latitude}, ${longitude}`, margin, canvas.height - 60);
    context.fillText(`${nowDate}`, margin, canvas.height - 30);

    // segundo layer preto
    context.fillStyle = 'rgba(0, 0, 0, 0.7)';

    addressLines.reverse().forEach((line, idx) => {
      context.fillText(
        line,
        margin + 1,
        canvas.height - 1 - padding * (idx + 3),
      );
    });

    context.fillText(
      `${latitude}, ${longitude}`,
      margin + 1,
      canvas.height - 61,
    );
    context.fillText(`${nowDate}`, margin + 1, canvas.height - 31);

    return canvas.toDataURL();
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (err) =>
        reject(new Error('Erro ao carregar imagem: ' + err));
    });
  }

  getCurrentDateTime() {
    const now = new Date();

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  getLines(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + ' ' + word).width;
      if (width < maxWidth) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  async editPlacesModal() {
    const modal = await this.modalCtrl.create({
      component: FuelPlacesModalComponent,
    });

    await modal.present();

    const { role, data } = await modal.onWillDismiss();

    if (role === 'confirm') {
      this.fuelForm.patchValue({
        fuelAddressId: data.id,
      });
      this.fuelPlaceLabel = data.local;
    }
  }

  async getAddress(): Promise<{
    latitude: number;
    longitude: number;
    address: string | null;
  }> {
    const msgLoadingAddress = await firstValueFrom(this.translate.get("REGISTER-FUEL--FETCHING-ADDRESS"));
    const loading = await this.loadingCtrl.create({
      message: msgLoadingAddress,
    });

    await loading.present();

    try {
      const { coords } = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });

      if (coords.accuracy && coords.accuracy >= 50) {
        const msgLocation = await firstValueFrom(this.translate.get("REGISTER-FUEL--LOCATION-INACCURATE"));

        await this.util.showToastWarning(
          msgLocation,
        );
      }

      const { latitude, longitude } = coords;

      const url = `https://web.hapolo.com.br/api/endereco_pretty?format=json&lat=${latitude}&lon=${longitude}`;

      const response = await firstValueFrom(this.http.get<any>(url));

      let address = '';

      if (response.success) {
        address = response.msg || '';
      }

      return { latitude, longitude, address };
    } catch (error) {
      console.error('Erro ao obter localização ou endereço:', error);
      return { latitude: 0, longitude: 0, address: null };
    } finally {
      await loading.dismiss();
    }
  }

  async submitFuelRegister() {
    console.log(this.fuelForm.value);

    if (!this.fuelForm.valid) {
      this.fuelForm.markAllAsTouched();

      const msgInvalid = await firstValueFrom(this.translate.get("REGISTER-FUEL--INVALID-FIELDS"));
      await this.util.showToastWarning(msgInvalid);
      return;
    }

    const hodometerValue = this.fuelForm.get('hodometer').value;
    const horimetroValue = this.fuelForm.get('horimetro').value;
    const isVehicleInfoInformed = hodometerValue || horimetroValue;

    if (!isVehicleInfoInformed) {
      const msgOdometer = await firstValueFrom(this.translate.get("REGISTER-FUEL--ODOMETER-OR-HOURMETER-REQUIRED"));
      await this.util.showToastWarning(
        msgOdometer,
      );
      return;
    }

    const msgSaving = await firstValueFrom(this.translate.get("REGISTER-FUEL--SAVING"));

    const loading = await this.loadingCtrl.create({
      message: msgSaving,
    });

    await loading.present();

    const requestData = {
      customerId: this.customerId,
      ...this.fuelForm.value,
    };

    const request$ = this.bridgeApi.post('api/fuel-register', requestData);

    request$.subscribe({
      next: async (data: any) => {
        await loading.dismiss();

        if (!data.success) {
          const msgTry = await firstValueFrom(this.translate.get("REGISTER-FUEL--TRY-AGAIN-LATER"));

          await this.util.showToastError(msgTry);
          return;
        }

        const msgRegistered = await firstValueFrom(this.translate.get("REGISTER-FUEL--FUEL-REGISTERED"));

        await this.util.showToastSuccess(msgRegistered);
        window.history.back();
      },
      error: async (error) => {
        console.error(error);

        await loading.dismiss();

        const msgTry = await firstValueFrom(this.translate.get("REGISTER-FUEL--TRY-AGAIN-LATER"));
        await this.util.showToastError(msgTry);
      },
    });
  }
}
